import { createContext, FC, useContext, useEffect, useState } from "react"
import { WithChildren } from "../../../_metronic/helpers"
import MyModal from "@common-utils/MyModal"
import axios from "axios"
import { API_URL_ESPACE_CONSULTANT } from "../../constants"
import { useAuth } from "../../modules/auth"

const CONST_CSE_NOTIFICATION_DISMISSED_KEY = "cse-notification-dismissed-v1"
const TEMPEOS_LOGIN_URL = "https://app.tempeos.com/login"
// Define activation date
const CSE_ACTIVATION_DATE = new Date("2026-01-01T00:00:00")

type AppConsultantContextProps = {
     dismissCseNotification: () => void
}

const AppConsultantContext = createContext<AppConsultantContextProps>({
     dismissCseNotification: () => {},
})

const useAppConsultant = () => {
     return useContext(AppConsultantContext)
}

const AppConsultantProvider: FC<WithChildren> = ({ children }) => {
     const [showCseNotification, setShowCseNotification] = useState<boolean>(false)
     const { currentUser } = useAuth()

     useEffect(() => {
          // Check if current date is before activation date
          if (new Date() < CSE_ACTIVATION_DATE) {
               return
          }

          const isDismissed = localStorage.getItem(CONST_CSE_NOTIFICATION_DISMISSED_KEY) === "true"
          if (!isDismissed && !currentUser?.fournisseur) {
               setShowCseNotification(true)
          }
     }, [])

     const dismissCseNotification = () => {
          // 1. Increment the counter in backend (Fire and forget)
          axios.post(`${API_URL_ESPACE_CONSULTANT}/seen-communication-tempeos`).catch(error => {
               console.error("Erreur lors de l'incrémentation du compteur CSE", error)
          })

          // 2. Close modal and save preference to LocalStorage
          localStorage.setItem(CONST_CSE_NOTIFICATION_DISMISSED_KEY, "true")
          setShowCseNotification(false)
     }

     const handleAccessTempeos = () => {
          // Open Tempeos in a new tab
          window.open(TEMPEOS_LOGIN_URL, "_blank", "noopener,noreferrer")
          // Close the modal and mark as seen
          dismissCseNotification()
     }

     return (
          <AppConsultantContext.Provider value={{ dismissCseNotification }}>
               {children}

               <MyModal show={showCseNotification} onCloseCallback={() => setShowCseNotification(false)} centered={true} backdrop="static" keyboard={false}>
                    <div className="d-flex flex-center flex-column py-5 px-5">
                         {/* Provider Logo */}
                         <div className="mb-5">
                              <img src="https://tempeos.com/wp-content/uploads/2021/03/logo-tempeos-300.png" className="h-60px" alt="Logo Tempeos" />
                         </div>

                         {/* Text Content */}
                         <div className="mb-8 text-center">
                              <h2 className="mb-3 text-gray-900">Notre partenaire CE externalisé !</h2>
                              <div className="text-gray-600 fs-5 fw-bold px-2">
                                   Nous avons le plaisir de vous annoncer que la gestion de votre CE est désormais confiée à <span className="text-primary">Tempeos</span>.
                              </div>
                              <div className="text-muted fs-6 mt-4">Vous pouvez y accéder à tout moment depuis le menu ou directement via le bouton ci-dessous.</div>
                         </div>

                         {/* Action Buttons */}
                         <div className="d-flex flex-column flex-sm-row gap-3 w-100 justify-content-center">
                              {/* Secondary Button: Close/Later */}
                              <button type="button" className="btn btn-light btn-active-light-primary fw-bold" onClick={() => setShowCseNotification(false)}>
                                   Consulter plus tard
                              </button>

                              {/* Primary Button: Redirect & Close */}
                              <button type="button" className="btn btn-primary fw-bolder px-6" onClick={handleAccessTempeos}>
                                   <i className="fas fa-external-link-alt fs-4 me-2"></i>
                                   Accéder à mon espace
                              </button>
                         </div>
                    </div>
               </MyModal>
          </AppConsultantContext.Provider>
     )
}

export { useAppConsultant, AppConsultantProvider, CONST_CSE_NOTIFICATION_DISMISSED_KEY }
